import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Spin,
  Form,
  Input,
  Space,
  Typography,
  Popconfirm,
  message,
  Row,
  Col,
  Divider,
  Tag,
  Alert,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { Category } from '../../api/CRMApiClient';
import ERPLayout from '../../components/layout/ERPLayout';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CategoryDetail: React.FC = observer(() => {
  const { t } = useTranslation();
  const { id, categoryId } = useParams<{ id?: string; categoryId?: string }>();
  const navigate = useNavigate();
  const { crmStore, applicationStore } = useStore();

  const currentId = id || categoryId;
  const isCreateMode = !currentId || currentId === 'new';

  const categoryIdNumber = useMemo(
    () => (!isCreateMode && currentId && !isNaN(Number(currentId)) ? Number(currentId) : null),
    [isCreateMode, currentId],
  );

  const [form] = Form.useForm<Category>();
  const [editing, setEditing] = useState<boolean>(isCreateMode);
  const [loading, setLoading] = useState<boolean>(true);

  const tenant = applicationStore.company?.Tenant || '';

  const getBackPath = () => (tenant ? `/${tenant}/crm/categories` : `/crm/categories`);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        crmStore.getCategories(),
        crmStore.getExternalCompanies(),
      ]);

      if (!isCreateMode && categoryIdNumber !== null) {
        const found = crmStore.categories.find((c) => c.id === categoryIdNumber);
        if (found) {
          form.setFieldsValue(found);
        } else {
          message.error(t('CategoryNotFound'));
          navigate(getBackPath());
        }
      } else {
        form.setFieldsValue({ name: '', description: '' });
      }
      setLoading(false);
    };

    loadData();
  }, [crmStore, isCreateMode, categoryIdNumber, form]);

  const assignedCompanies = useMemo(() => {
    if (isCreateMode || categoryIdNumber === null) return [];
    return crmStore.externalCompanies.filter((c) => c.categoryId === categoryIdNumber);
  }, [crmStore.externalCompanies, isCreateMode, categoryIdNumber]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      if (isCreateMode) {
        const created = await crmStore.addCategory(values);
        message.success(t('CreatedSuccessfully'));
        if (created && typeof created === 'object' && 'id' in created && (created as any).id) {
          navigate(tenant ? `/${tenant}/crm/category/${(created as any).id}` : `/crm/category/${(created as any).id}`);
        } else {
          navigate(getBackPath());
        }
      } else if (categoryIdNumber !== null) {
        await crmStore.updateCategory(categoryIdNumber, values);
        message.success(t('SavedSuccessfully'));
        setEditing(false);
      }
    } catch {
      // validation error handled by Ant Design Form
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate(getBackPath());
    } else {
      if (categoryIdNumber !== null) {
        const found = crmStore.categories.find((c) => c.id === categoryIdNumber);
        if (found) form.setFieldsValue(found);
      }
      setEditing(false);
    }
  };

  const onDelete = async () => {
    if (categoryIdNumber === null) return;
    const ok = await crmStore.deleteCategory(categoryIdNumber);
    if (ok) {
      message.success(t('DeletedSuccessfully'));
      navigate(getBackPath());
    }
  };

  const breadcrumbItems = [
    { label: t('Home'), path: tenant ? `/${tenant}` : '/' },
    { label: 'CRM', path: tenant ? `/${tenant}/crm` : '/crm' },
    { label: t('Categories'), path: getBackPath() },
    {
      label: isCreateMode ? t('AddCategory') : t('CategoryDetails'),
      path: isCreateMode
        ? (tenant ? `/${tenant}/crm/category/new` : `/crm/category/new`)
        : (tenant ? `/${tenant}/crm/category/${categoryIdNumber}` : `/crm/category/${categoryIdNumber}`),
    },
  ];

  return (
    <ERPLayout breadcrumbTitle={isCreateMode ? t('AddCategory') : t('CategoryDetails')} breadcrumbItems={breadcrumbItems}>
      <Spin spinning={loading}>
        <Card
          title={
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(getBackPath())}
              />
              <Title level={4} style={{ margin: 0 }}>
                {isCreateMode
                  ? t('AddCategory')
                  : crmStore.categories.find((c) => c.id === categoryIdNumber)?.name || t('CategoryDetails')}
              </Title>
            </Space>
          }
          extra={
            <Space>
              {editing ? (
                <>
                  <Button type="primary" icon={<SaveOutlined />} onClick={onSave}>
                    {t('Save')}
                  </Button>
                  <Button onClick={handleCancel}>{t('Cancel')}</Button>
                </>
              ) : (
                <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                  {t('Edit')}
                </Button>
              )}
              {!isCreateMode && (
                <Popconfirm title={t('DeleteCategoryConfirm')} onConfirm={onDelete}>
                  <Button danger icon={<DeleteOutlined />}>
                    {t('Delete')}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          }
        >
          <div style={{ paddingTop: 8 }}>
            <Row gutter={[32, 24]}>
              {/* Left Column: Form inputs */}
              <Col xs={24} lg={14}>
                <Form form={form} layout="vertical" disabled={!editing}>
                  <Divider orientation="left" style={{ marginTop: 0 }}>
                    {t('GeneralInformation')}
                  </Divider>
                  <Form.Item
                    name="name"
                    label={t('Name')}
                    rules={[{ required: true, message: t('PleaseEnterName') }]}
                  >
                    <Input placeholder={t('EnterCategoryName')} />
                  </Form.Item>
                  <Form.Item name="description" label={t('Description')}>
                    <TextArea rows={6} placeholder={t('EnterDescription')} />
                  </Form.Item>
                </Form>
              </Col>

              {/* Right Column: Related Entities (Matching EventDetail style) */}
              <Col xs={24} lg={10}>
                <Divider orientation="left" style={{ marginTop: 0 }}>
                  {t('RelatedEntities')}
                </Divider>

                <Card
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={
                    <Space>
                      <BankOutlined />
                      {t('Companies')} ( {assignedCompanies.length} )
                    </Space>
                  }
                >
                  {assignedCompanies.length > 0 ? (
                    assignedCompanies.map((comp, idx) => (
                      <div
                        key={comp.id}
                        style={{
                          marginBottom:
                            idx < assignedCompanies.length - 1 ? 12 : 0,
                          borderBottom:
                            idx < assignedCompanies.length - 1
                              ? '1px solid #f0f0f0'
                              : 'none',
                          paddingBottom:
                            idx < assignedCompanies.length - 1 ? 8 : 0,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <strong style={{ fontSize: 14 }}>{comp.name}</strong>
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              navigate(
                                tenant
                                  ? `/${tenant}/crm/company/${comp.id}`
                                  : `/crm/company/${comp.id}`,
                              )
                            }
                          >
                            {t('View')}
                          </Button>
                        </div>
                        {comp.edrpou && (
                          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#555' }}>
                            <strong>{t('EDRPOU')}:</strong> {comp.edrpou}
                          </p>
                        )}
                        {comp.addresses && comp.addresses.length > 0 && (
                          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#555' }}>
                            <strong>{t('Address')}:</strong>{' '}
                            {[
                              comp.addresses[0].locationName,
                              comp.addresses[0].city,
                              comp.addresses[0].country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {t('NoCompaniesInCategory')}
                    </Text>
                  )}
                </Card>

                <Alert
                  message={t('CategoryTip')}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  style={{ borderRadius: 8 }}
                />
              </Col>
            </Row>
          </div>
        </Card>
      </Spin>
    </ERPLayout>
  );
});

export default CategoryDetail;
